import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { Loader2, RefreshCw, TrendingUp, TrendingDown, DollarSign, Percent } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

const chartConfig = {
  paid: { label: "محصل", color: "hsl(var(--primary))" },
  overdue: { label: "متأخر", color: "hsl(var(--destructive))" },
};

const MonthlyPaymentReport = () => {
  const { profile } = useAuth();
  const nurseryId = profile?.nursery_id;
  const queryClient = useQueryClient();
  const [generating, setGenerating] = useState(false);

  const { data: reports, isLoading } = useQuery({
    queryKey: ["monthly-payment-reports", nurseryId],
    queryFn: async () => {
      if (!nurseryId) return [];
      const { data, error } = await supabase
        .from("monthly_payment_reports" as any)
        .select("*")
        .eq("nursery_id", nurseryId)
        .order("year", { ascending: false })
        .order("month", { ascending: false })
        .limit(12);
      if (error) throw error;
      return (data || []) as any[];
    },
    enabled: !!nurseryId,
  });

  const latest = reports?.[0];

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const now = new Date();
      let month = now.getMonth(); // previous month (0-indexed)
      let year = now.getFullYear();
      if (month === 0) { month = 12; year -= 1; } 

      const { data, error } = await supabase.functions.invoke("generate-monthly-payment-report", {
        body: { month, year },
      });
      if (error) throw error;
      toast.success("تم إنشاء التقرير بنجاح");
      queryClient.invalidateQueries({ queryKey: ["monthly-payment-reports"] });
    } catch (e: any) {
      toast.error("فشل إنشاء التقرير: " + (e.message || "خطأ غير معروف"));
    } finally {
      setGenerating(false);
    }
  };

  const barData = (reports || [])
    .slice(0, 6)
    .reverse()
    .map((r: any) => ({
      label: `${r.month}/${r.year}`,
      paid: r.paid_amount,
      overdue: r.overdue_amount,
    }));

  const monthNames: Record<number, string> = {
    1: "يناير", 2: "فبراير", 3: "مارس", 4: "أبريل", 5: "مايو", 6: "يونيو",
    7: "يوليو", 8: "أغسطس", 9: "سبتمبر", 10: "أكتوبر", 11: "نوفمبر", 12: "ديسمبر",
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-foreground">التقرير المالي الشهري</h2>
          {latest && (
            <p className="text-sm text-muted-foreground">
              آخر تقرير: {monthNames[latest.month]} {latest.year}
            </p>
          )}
        </div>
        <Button onClick={handleGenerate} disabled={generating} variant="outline" className="gap-2">
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          إنشاء تقرير الآن
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : !latest ? (
        <Card className="shadow-card">
          <CardContent className="p-8 text-center text-muted-foreground">
            لا توجد تقارير بعد. اضغط "إنشاء تقرير الآن" لإنشاء أول تقرير.
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "المبلغ المحصل", value: `${Number(latest.paid_amount).toLocaleString()} ج.م`, icon: DollarSign, color: "text-primary" },
              { label: "المبلغ المتأخر", value: `${Number(latest.overdue_amount).toLocaleString()} ج.م`, icon: TrendingDown, color: "text-destructive" },
              { label: "نسبة التحصيل", value: `${latest.collection_rate}%`, icon: Percent, color: "text-primary" },
              { label: "صافي الربح", value: `${Number(latest.net_profit).toLocaleString()} ج.م`, icon: TrendingUp, color: Number(latest.net_profit) >= 0 ? "text-primary" : "text-destructive" },
            ].map((s, i) => (
              <Card key={i} className="shadow-card">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                    <s.icon className={`w-5 h-5 ${s.color}`} />
                  </div>
                  <div>
                    <div className="text-xl font-bold text-foreground">{s.value}</div>
                    <div className="text-xs text-muted-foreground">{s.label}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Detail Card */}
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="text-lg">تفاصيل تقرير {monthNames[latest.month]} {latest.year}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div className="p-3 bg-muted rounded-lg">
                  <div className="text-muted-foreground">إجمالي الفواتير</div>
                  <div className="font-bold text-foreground">{latest.total_invoices} فاتورة</div>
                  <div className="text-muted-foreground">{Number(latest.total_amount).toLocaleString()} ج.م</div>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <div className="text-muted-foreground">المدفوع</div>
                  <div className="font-bold text-primary">{latest.paid_count} فاتورة</div>
                  <div className="text-primary">{Number(latest.paid_amount).toLocaleString()} ج.م</div>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <div className="text-muted-foreground">المتأخر</div>
                  <div className="font-bold text-destructive">{latest.overdue_count} فاتورة</div>
                  <div className="text-destructive">{Number(latest.overdue_amount).toLocaleString()} ج.م</div>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <div className="text-muted-foreground">إجمالي المصروفات</div>
                  <div className="font-bold text-foreground">{Number(latest.total_expenses).toLocaleString()} ج.م</div>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <div className="text-muted-foreground">إجمالي الرواتب</div>
                  <div className="font-bold text-foreground">{Number(latest.total_payroll).toLocaleString()} ج.م</div>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <div className="text-muted-foreground">صافي الربح</div>
                  <div className={`font-bold ${Number(latest.net_profit) >= 0 ? "text-primary" : "text-destructive"}`}>
                    {Number(latest.net_profit).toLocaleString()} ج.م
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bar Chart - last 6 months */}
          {barData.length > 1 && (
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="text-lg">مقارنة المحصل والمتأخر (آخر 6 أشهر)</CardTitle>
              </CardHeader>
              <CardContent>
                <ChartContainer config={chartConfig} className="h-[300px] w-full">
                  <BarChart data={barData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="label" className="text-xs" />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="paid" fill="var(--color-paid)" radius={[4, 4, 0, 0]} name="محصل" />
                    <Bar dataKey="overdue" fill="var(--color-overdue)" radius={[4, 4, 0, 0]} name="متأخر" />
                  </BarChart>
                </ChartContainer>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default MonthlyPaymentReport;
